import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { omit, uniqueId } from "lodash";
import { useFormik } from "formik";

import { getOptionLabel } from "common/enums";
import ChargesTypeItem from "./ChargesTypeItem";
import InputFieldText from "Components/Common/InputFieldText";
import {
  SUPER_ADMIN,
  powerAdmin2Roles,
  powerAdmin3Roles,
} from "common/constant";

import { filterEmptyStrings, isAuthorizedUser, onlyNumberKeys } from "utils";

import {
  useGetApplicationRefundCalculations,
  usePostRefundCalculationMutation,
  usePutRefundCalculationMutation,
} from "apis/ApplicationRequestDetail";

const TemporaryConnectionRefundCalculations = ({
  applicationId = "",
  utilityConsumerId,
  applicationsData: {
    isDTA,
    isIGSTExempted,
    utilityConsumerApplications: applications = [],
  },
  handleClose,
  t,
}) => {
  const { roles } = useSelector((state) => state.Login);

  const [addRefundCalculations, { isLoading }] =
    usePostRefundCalculationMutation();

  const [updateRefundCalculations, { isLoading: isRefundCalculationUpdating }] =
    usePutRefundCalculationMutation();

  const {
    data: { data: refundCalculationsData } = {},
    isLoading: isRefundCalculationDataLoading,
  } = useGetApplicationRefundCalculations(
    filterEmptyStrings({
      applicationId,
      utilityConsumerId,
    })
  );

  const initialValuesOfAccessories = (data, isEdit) => {
    if (data?.length) {
      return data.map(
        ({ id, accessoryId, quantity, rate, finalCost, measurementType }) => ({
          id,
          accessoryId,
          rate,
          finalCost: finalCost ?? 0,
          quantity,
          measurementTypeId: measurementType.id,
        })
      );
    } else if (!isEdit) {
      return [
        {
          id: uniqueId("temp_"),
          accessoryId: "",
          quantity: "",
          measurementTypeId: "",
          rate: "",
          finalCost: 0,
        },
      ];
    } else {
      return [];
    }
  };

  const updatedApplications = useMemo(
    () =>
      applications?.length
        ? [
            ...applications,
            {
              name: "Break up of recovered material",
              isDTA: applications.at(-1)?.isDTA ?? false,
              isIGSTExempted: applications.at(-1)?.isIGSTExempted ?? true,
            },
          ]
        : [
            {
              name: "Break up of recovered material",
              isDTA: isDTA,
              isIGSTExempted: isIGSTExempted,
            },
          ],
    [applications]
  );

  const isDisabled = useMemo(
    () => isAuthorizedUser(roles, powerAdmin2Roles),
    [roles]
  );

  const initialValues = useMemo(
    () => ({
      applicationId: refundCalculationsData?.applicationId ?? applicationId,
      billAmount:
        refundCalculationsData?.utilityConsumerDetails?.billAmount ?? 0,
      consumerApplications: refundCalculationsData
        ?.applicationRecoveryMaterialCosts?.length
        ? refundCalculationsData.applicationRecoveryMaterialCosts.map(
            ({
              materialRecoverApplicationTypeId,
              materialRecoverApplicationId,
              isDTA,
              materialAccessories = [],
              labourAccessories = [],
              depreciatedCostOfRecoveredMaterial,
              isIGSTExempted,
            }) => ({
              applicationType: {
                id: materialRecoverApplicationTypeId,
                name: materialRecoverApplicationTypeId
                  ? getOptionLabel(
                      "powerApplicationTypes",
                      materialRecoverApplicationTypeId
                    )
                  : "Break up of recovered material",
                applicationId: materialRecoverApplicationId,
              },
              totalMaterialCharges: 0,
              isDTA,
              isIGSTExempted,
              totalLabourCharges: 0,
              totalCharges: 0,
              supervisionCharge: 0,
              sgst: 0,
              cgst: 0,
              recoveredMaterialCost: depreciatedCostOfRecoveredMaterial,
              igst: 0,
              disconnectionCharge: 0,
              finalAmount: 0,
              materialChargesAccessoriesList: initialValuesOfAccessories(
                materialAccessories ?? [],
                !!refundCalculationsData
              ),
              labourChargesAccessoriesList: initialValuesOfAccessories(
                labourAccessories ?? [],
                !!refundCalculationsData
              ),
            })
          )
        : updatedApplications?.length
        ? updatedApplications.map(
            ({
              applicationType,
              name,
              applicationId,
              isDTA,
              isIGSTExempted,
            }) => ({
              applicationType: {
                id: applicationType,
                name:
                  name ??
                  getOptionLabel("powerApplicationTypes", applicationType),
                applicationId,
              },
              totalMaterialCharges: 0,
              isDTA,
              isIGSTExempted,
              totalLabourCharges: 0,
              totalCharges: 0,
              supervisionCharge: 0,
              sgst: 0,
              cgst: 0,
              igst: 0,
              disconnectionCharge: 0,
              finalAmount: 0,
              materialChargesAccessoriesList: initialValuesOfAccessories(
                refundCalculationsData?.applicationRecoveryMaterialCosts
                  ?.materialAccessories ?? []
              ),
              labourChargesAccessoriesList: initialValuesOfAccessories(
                refundCalculationsData?.applicationRecoveryMaterialCosts
                  ?.labourAccessories ?? []
              ),
            })
          )
        : [],
      refundCalculation: {
        id: refundCalculationsData?.refundCalculation?.id ?? 0,
        temporaryConnectionDeposit:
          refundCalculationsData?.refundCalculation
            ?.temporaryConnectionDeposit ?? 0,
        depreciatedCostOfRecoveredMaterial:
          refundCalculationsData?.refundCalculation
            ?.depreciatedCostOfRecoveredMaterial ?? 0,
        applicationMaterialCost: refundCalculationsData?.refundCalculation
          ?.applicationMaterialAndLabourCosts ?? { 0: 0 },
        disconnectionCharge:
          refundCalculationsData?.refundCalculation?.disconnectionCharge ?? 0,
        pendingDues:
          refundCalculationsData?.refundCalculation?.pendingDues ?? 0,
        finalAmount:
          refundCalculationsData?.refundCalculation?.finalAmount ?? 0,
        recoveryMaterialCost:
          refundCalculationsData?.refundCalculation?.recoveryMaterialCost ?? 0,
      },
    }),
    [updatedApplications, refundCalculationsData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,
    validationSchema: Yup.object().shape({
      consumerApplications: Yup.array().of(
        Yup.object().shape({
          materialChargesAccessoriesList: Yup.array().of(
            Yup.object().shape({
              accessoryId: Yup.string().required("Accessory is required"),
              quantity: Yup.number()
                .min(1, "Quantity must be greater than 0")
                .required("Quantity is required"),
              measurementTypeId: Yup.string().required("Unit is required"),
              rate: Yup.number().required("Rate is required"),
            })
          ),
          labourChargesAccessoriesList: Yup.array().of(
            Yup.object().shape({
              accessoryId: Yup.string().required("Accessory is required"),
              quantity: Yup.number()
                .min(1, "Quantity must be greater than 0")
                .required("Quantity is required"),
              measurementTypeId: Yup.number().required("Unit is required"),
              rate: Yup.number().required("Rate is required"),
            })
          ),
        })
      ),
      refundCalculation: Yup.object({
        depreciatedCostOfRecoveredMaterial: Yup.number().required(
          "Depreciated value of total cost of recovered material is required"
        ),
        disconnectionCharge: Yup.number().required(
          "Disconnection charges is required"
        ),
        applicationMaterialCost: Yup.object({
          0: Yup.number().test("isValidValue", "", function (_, context) {
            const [{ value } = {}] = context.from || [];
            if (!applications?.length && !value[0]) {
              return context.createError({
                message:
                  "Total actual cost incurred for developing network is required",
              });
            }
            return true;
          }),
        }),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          applicationId: values.applicationId,
          utilityConsumerId,
          applicationMaterialRecoveryCosts: values.consumerApplications.map(
            ({
              applicationType,
              materialChargesAccessoriesList,
              labourChargesAccessoriesList,
              totalLabourCharges,
              totalMaterialCharges,
              supervisionCharge,
              sgst,
              cgst,
              igst,
              finalAmount,
            }) => ({
              materialRecoverApplicationId: applicationType.applicationId,
              materialAccessories: materialChargesAccessoriesList.map(
                ({
                  id,
                  accessoryId,
                  measurementTypeId,
                  rate,
                  quantity,
                  finalCost,
                }) => ({
                  id: id.toString().startsWith("temp") ? 0 : id,
                  accessoryId,
                  measurementTypeId,
                  rate,
                  quantity,
                  finalCost,
                })
              ),
              labourAccessories: labourChargesAccessoriesList.map(
                ({
                  id,
                  accessoryId,
                  measurementTypeId,
                  rate,
                  quantity,
                  finalCost,
                }) => ({
                  id: id.toString().startsWith("temp") ? 0 : id,
                  accessoryId,
                  measurementTypeId,
                  rate,
                  quantity,
                  finalCost,
                })
              ),
              totalMaterialAndLabourCost:
                totalLabourCharges + totalMaterialCharges,
              supervisionCharge,
              sgst,
              cgst,
              igst,
              finalAmount,
            })
          ),
          refundCalculation: values.refundCalculation,
        };
        const updatedPayload = filterEmptyStrings(
          omit(payload, [
            applications.length
              ? "refundCalculation.applicationMaterialCost.0"
              : "",
          ])
        );
        if (roles.includes(SUPER_ADMIN)) {
          await updateRefundCalculations(updatedPayload);
        } else {
          await addRefundCalculations(updatedPayload);
        }
        handleClose();
      } catch (err) {}
    },
  });

  const {
    values: { consumerApplications, billAmount },
    handleSubmit,
    setFieldValue,
  } = formik;

  return (
    <>
      {isRefundCalculationDataLoading ? (
        <div className="d-flex justify-content-center mt-3">
          <CircularProgress size={30} />
        </div>
      ) : (
        <>
          {consumerApplications.map(
            (
              {
                applicationType,
                isDTA,
                isIGSTExempted,
                materialChargesAccessoriesList,
                labourChargesAccessoriesList,
                totalMaterialCharges,
                totalLabourCharges,
                totalCharges,
                supervisionCharge,
                sgst,
                cgst,
                igst,
                finalAmount,
              },
              index
            ) => (
              <React.Fragment key={index}>
                <Grid
                  container
                  columnSpacing={1}
                  className="border border-2 ps-2 pb-2 p-3 mb-2 mt-2"
                >
                  <div>
                    <Typography className="mb-3" lineHeight="1" variant="h6">
                      {t(applicationType.name)}
                    </Typography>
                  </div>

                  <ChargesTypeItem
                    applicationData={{
                      applicationType,
                      accessoriesList: materialChargesAccessoriesList,
                      totalMaterialCharges,
                      totalCharges,
                      supervisionCharge,
                      sgst,
                      isDTA,
                      isIGSTExempted,
                      cgst,
                      igst,
                      finalAmount,
                      billAmount,
                    }}
                    applications={applications}
                    isDisabled={isDisabled}
                    headerName="Material Charges"
                    formik={formik}
                    isMaterialCharge
                    index={index}
                    keyName={`consumerApplications[${index}].materialChargesAccessoriesList`}
                  />

                  <ChargesTypeItem
                    applicationData={{
                      applicationType,
                      accessoriesList: labourChargesAccessoriesList,
                      totalLabourCharges,
                      totalCharges,
                      supervisionCharge,
                      isIGSTExempted,
                      isDTA,
                      sgst,
                      cgst,
                      igst,
                      finalAmount,
                      billAmount,
                    }}
                    applications={applications}
                    isDisabled={isDisabled}
                    headerName="Labour Charges"
                    formik={formik}
                    index={index}
                    keyName={`consumerApplications[${index}].labourChargesAccessoriesList`}
                  />

                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      formik={formik}
                      label={t("total Charges")}
                      name={`consumerApplications[${index}].totalCharges`}
                      placeholder="Enter Total Charges"
                      type="number"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      formik={formik}
                      label={t("Supervision Charges")}
                      name={`consumerApplications[${index}].supervisionCharge`}
                      placeholder="Enter SuperVision Charges"
                      type="number"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      formik={formik}
                      label={t("State GST Charges")}
                      name={`consumerApplications[${index}].sgst`}
                      placeholder="Enter State GST"
                      type="number"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      formik={formik}
                      label={t("Central GST Charges")}
                      name={`consumerApplications[${index}].cgst`}
                      placeholder="Enter Central GST"
                      type="number"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      formik={formik}
                      label={t("IGST Charges")}
                      name={`consumerApplications[${index}].igst`}
                      placeholder="Enter IGST"
                      type="number"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      formik={formik}
                      label={t("Sub Total")}
                      name={`consumerApplications[${index}].finalAmount`}
                      placeholder="Enter Sub Total"
                      type="number"
                      disabled
                    />
                  </Grid>
                  {!applicationType.id && (
                    <Grid item xs={12}>
                      <InputFieldText
                        formik={formik}
                        label={t(
                          "Depreciated value of Total cost of recovered material"
                        )}
                        name={`consumerApplications[${index}].recoveredMaterialCost`}
                        onChange={(e) => {
                          setFieldValue(
                            `consumerApplications[${index}].recoveredMaterialCost`,
                            +e.target.value
                          );
                          setFieldValue(
                            "refundCalculation.depreciatedCostOfRecoveredMaterial",
                            +e.target.value
                          );
                        }}
                        placeholder="Enter Depreciated value of Total cost of recovered material"
                        type="number"
                        InputProps={{
                          inputProps: {
                            min: 0,
                            onKeyDown: (e) => onlyNumberKeys(e, true),
                          },
                        }}
                        disabled={isDisabled}
                      />
                    </Grid>
                  )}
                </Grid>
              </React.Fragment>
            )
          )}
          <Grid
            container
            columnSpacing={1}
            className="border border-2 ps-2 pb-2 p-3 mb-2 mt-2"
          >
            <Grid xs={12}>
              <Typography className="mb-3" lineHeight="1" variant="h6">
                {t("Final Bill")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <InputFieldText
                formik={formik}
                label={t(
                  "Total amount received from customer (Temporary Connection Deposit)"
                )}
                name="refundCalculation.temporaryConnectionDeposit"
                placeholder="Total amount received from customer (Temporary Connection Deposit)"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                disabled={isDisabled}
              />
              {applications?.length ? (
                applications.map(({ applicationId, applicationType }) => (
                  <Grid item xs={12} key={applicationId}>
                    <InputFieldText
                      formik={formik}
                      label={t(
                        `Total Actual cost incurred for developing network for ${getOptionLabel(
                          "powerApplicationTypes",
                          applicationType
                        )}`
                      )}
                      name={`refundCalculation.applicationMaterialCost.${applicationId}`}
                      placeholder={`Total Actual cost incurred for developing network for ${getOptionLabel(
                        "powerApplicationTypes",
                        applicationType
                      )}`}
                      type="number"
                      disabled
                    />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <InputFieldText
                    formik={formik}
                    label={t(
                      "Total Actual cost incurred for developing network"
                    )}
                    name="refundCalculation.applicationMaterialCost.0"
                    placeholder="Total Actual cost incurred for developing network"
                    disabled={isDisabled}
                    type="number"
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <InputFieldText
                formik={formik}
                label={t(
                  "Depreciated value of Total cost of recovered material"
                )}
                name="refundCalculation.depreciatedCostOfRecoveredMaterial"
                placeholder={
                  "Depreciated value of Total cost of recovered material"
                }
                disabled={isDisabled}
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputFieldText
                formik={formik}
                label={t(
                  "Total labour cost incurred for recovery of material and connection removal"
                )}
                name="refundCalculation.recoveryMaterialCost"
                placeholder={
                  "Total labour cost incurred for recovery of material and connection removal"
                }
                type="number"
                disabled
              />
            </Grid>

            <Grid item xs={6}>
              <InputFieldText
                formik={formik}
                label={t("Disconnection Charges")}
                name="refundCalculation.disconnectionCharge"
                placeholder="Enter Disconnection Charges"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                disabled={isDisabled}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldText
                formik={formik}
                label={t("Pending Dues (If Any)")}
                name="refundCalculation.pendingDues"
                placeholder="Enter Pending Dues (If Any)"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
                disabled={isAuthorizedUser(roles, powerAdmin3Roles)}
              />
            </Grid>
            <Grid item xs={12}>
              <InputFieldText
                formik={formik}
                name="billAmount"
                label={t("Bill Amount")}
                placeholder="Enter Bill Amount"
                type="number"
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <InputFieldText
                formik={formik}
                name="refundCalculation.finalAmount"
                label={t("Final Amount To Be Refunded To Consumer")}
                placeholder="Enter Final Amount To Be Refunded To Consumer"
                type="number"
                disabled
              />
            </Grid>
          </Grid>

          <Box className="d-flex justify-content-end">
            <Button
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || isRefundCalculationUpdating}
            >
              {t("Submit")}
              {(isLoading || isRefundCalculationUpdating) && (
                <CircularProgress size={20} />
              )}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

TemporaryConnectionRefundCalculations.propTypes = {
  applicationsData: PropTypes.object,
  applicationId: PropTypes.number,
  utilityConsumerId: PropTypes.number,
  t: PropTypes.func,
  handleClose: PropTypes.func,
};

export default withTranslation()(TemporaryConnectionRefundCalculations);
